import React from 'react';
import { ApolloError } from 'apollo-client';

import BoxBody from 'components/BoxBody';
import Button from 'components/ButtonDeprecated';
import Buttons from 'components/Buttons';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import CoachingParticipantEditField from 'coaching/components/CoachingCoordinatorView/CoachingParticipantEditField';
import CoachingSessionRow from 'coaching/components/CoachingCoordinatorView/CoachingSessionRow';
import Divider from 'components/Divider';
import ErrorReporter from 'app/components/ErrorReporter';
import EventTracking from 'lib/EventTracking';
import Heading from 'components/Heading';
import Icon from 'components/Icon';
import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';
import CoachingConstants from 'coaching/CoachingConstants';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import Text from 'components/Text';
import TextWithIcon from 'components/TextWithIcon';
import TimezoneHelper from 'lib/timezones';
import { CoachingUserType } from 'globalTypes';
import {
  CoachingSessionGQL,
  DistanceLearningSessionGQL,
} from 'coaching/containers/GetCoachingSessionsAndRecordingsContainer';
import { ParticipantRow } from 'coaching/components/CoachingCoordinatorView/CoachingCoordinatorPeopleListView';
import moment from 'moment-timezone';

export type Props = {
  participant: ParticipantRow;
  onShowScheduleSessionView?: () => void;
  upcomingSessions?: CoachingSessionGQL[];
  previousSessions?: CoachingSessionGQL[];
  sessionMedia?: DistanceLearningSessionGQL[];
  sessionsLoading: boolean;
  sessionsError?: ApolloError;
};

type State = {
  showUpcomingSessions: boolean;
  showPreviousSessions: boolean;
  showSessionMedia: boolean;
};

function formatRecordingDate(unixTimestampInMilliseconds: string): string {
  const timestampInSeconds = Number(unixTimestampInMilliseconds) / 1000;
  return moment.unix(timestampInSeconds).format('MMM DD, YYYY');
}

export default class CoachingCoordinatorParticipantSidebar extends React.Component<Props, State> {
  state = {
    showPreviousSessions: false,
    showUpcomingSessions: false,
    showSessionMedia: false,
  };

  trackEvent(event: string) {
    EventTracking.trackKey(`coaching.coordinator_tool.participant_sidebar.${event}`);
  }

  togglePreviousSessionsVisibility = () => {
    this.setState({ showPreviousSessions: !this.state.showPreviousSessions }, () => {
      if (this.state.showPreviousSessions) {
        this.trackEvent('past_sessions.view');
      }
    });
  };

  toggleUpcomingSessionsVisibility = () => {
    this.setState({ showUpcomingSessions: !this.state.showUpcomingSessions }, () => {
      if (this.state.showUpcomingSessions) {
        this.trackEvent('upcoming_sessions.view');
      }
    });
  };

  toggleSessionMediaVisibility = () => {
    this.setState({ showSessionMedia: !this.state.showSessionMedia }, () => {
      if (this.state.showSessionMedia) {
        this.trackEvent('recordings.view');
      }
    });
  };

  renderSessions = (
    showSessions: boolean,
    loading: boolean,
    isPast: boolean,
    sessions?: CoachingSessionGQL[],
    error?: ApolloError
  ) => {
    if (!showSessions) {
      return null;
    }

    const loadingState = this.renderLoading('sessions', loading, error, sessions);
    if (loadingState) {
      return loadingState;
    }

    const { participant } = this.props;

    return sessions!.map(session => (
      <CoachingSessionRow
        participantUuid={participant.uuid}
        userName={participant.studentName}
        userType={CoachingUserType.participant}
        session={session}
        isPastEvent={isPast}
        key={session.uuid}
      />
    ));
  };

  renderMedia = (showMedia: boolean, sessionMedia: DistanceLearningSessionGQL[]) => {
    if (!showMedia) {
      return null;
    }

    return (
      <BoxBody size="xxsmall">
        {sessionMedia.map((media: DistanceLearningSessionGQL) => (
          <Row size="xsmall" data-test="coaching-session-media">
            <Link target="_blank" href={media.s3ResourceUri}>
              <Text size="medium" data-test="coaching-session-media-recording-date">
                {formatRecordingDate(media.createdAt)}
              </Text>
            </Link>
          </Row>
        ))}
      </BoxBody>
    );
  };

  renderLoading = (
    dataType: 'sessions' | 'media',
    loading: boolean,
    error?: ApolloError,
    data?: any[]
  ) => {
    if (loading) {
      return <CenteredLoadingIndicator />;
    }

    if (error) {
      return (
        <ErrorReporter
          error={`CoachingCoordinatorParticipantSidebar - couldn't load upcoming ${dataType} error=${error}`}>
          <Text color="graphite">Could not load {dataType}</Text>
        </ErrorReporter>
      );
    }

    return null;
  };

  renderHeader = (headerText: string, showSessions: boolean, sessionsLength?: number) => {
    const iconName = showSessions ? 'chevron-down' : 'chevron-right';

    return (
      <Text color="chalkboard" element="div" lineHeight="flush">
        <MediaObject center figure="right">
          <Heading
            level="4"
            flush={!showSessions && sessionsLength !== undefined && sessionsLength > 0}>
            {headerText}{' '}
            {!this.props.sessionsLoading && sessionsLength !== undefined && `(${sessionsLength})`}
          </Heading>
          {sessionsLength === 0 ? <span /> : <Icon name={iconName} size="small" />}
        </MediaObject>
      </Text>
    );
  };

  renderEmptyList = (text: string) => {
    return (
      <Text color="graphite" size="small">
        {text}
      </Text>
    );
  };

  renderSessionSection = (
    sectionName: 'Past' | 'Upcoming',
    showSessions: boolean,
    toggleSessionVisibility: () => void,
    sessions?: CoachingSessionGQL[]
  ) => {
    const { sessionsLoading, sessionsError } = this.props;
    const noSessions = sessions?.length === 0;
    const isPast = sectionName === 'Past';
    return (
      <React.Fragment>
        <Link
          disabled={noSessions}
          onClick={noSessions ? undefined : toggleSessionVisibility}
          data-test={`toggle-sessions-${sectionName}`}>
          {this.renderHeader(sectionName, showSessions, sessions && sessions.length)}
        </Link>
        {noSessions
          ? this.renderEmptyList('No sessions yet')
          : this.renderSessions(showSessions, sessionsLoading, isPast, sessions, sessionsError)}
      </React.Fragment>
    );
  };

  renderSessionMediaSection = (
    showMedia: boolean,
    toggleVisibility: () => void,
    sessionMedia?: DistanceLearningSessionGQL[]
  ) => {
    const { sessionsLoading, sessionsError } = this.props;
    const media = sessionMedia?.slice(0, CoachingConstants.COACHING_SESSION_RECORDINGS_LIMIT) ?? [];
    const noMedia = media.length === 0;

    const loadingState = this.renderLoading('media', sessionsLoading, sessionsError, media);
    if (loadingState) {
      return loadingState;
    }

    return (
      <React.Fragment>
        <Link
          disabled={noMedia}
          onClick={noMedia ? undefined : toggleVisibility}
          data-test="toggle-sessions-media">
          {this.renderHeader('Recordings', showMedia, media.length)}
        </Link>
        {noMedia ? this.renderEmptyList('No recordings yet') : this.renderMedia(showMedia, media!)}
      </React.Fragment>
    );
  };

  render() {
    const { participant, onShowScheduleSessionView } = this.props;
    const adminLinks = [
      <Link
        href={`${LinkConstants.R101_ADMIN_USER_BY_UUID_URL}${participant.purchaserUuid}`}
        target="_blank"
        weight="semibold">
        User who purchased
      </Link>,
    ];
    if (participant.studentUuid) {
      adminLinks.push(
        <Link
          href={`${LinkConstants.R101_ADMIN_USER_BY_UUID_URL}${participant.studentUuid}`}
          target="_blank"
          weight="semibold">
          Student user
        </Link>
      );
    }

    return (
      <BoxBody size="small">
        <Heading level="2" serif>
          {participant.studentName}
        </Heading>
        <Divider size="medium" />
        <Row size="xxsmall">
          <Text color="graphite" size="xxxsmall" weight="semibold">
            Links to r101-admin
          </Text>
        </Row>
        {adminLinks.reduce((prev, curr) => (
          <>
            {prev} • {curr}
          </>
        ))}
        <Divider size="medium" />
        {participant.subjects.length > 0 ? (
          <Row size="small">
            <TextWithIcon icon="book">{participant.subjects.join(', ')}</TextWithIcon>
          </Row>
        ) : null}
        {participant.studentGradeLevel ? (
          <Row size="small">
            <TextWithIcon icon="apple">Grade {participant.studentGradeLevel}</TextWithIcon>
          </Row>
        ) : null}
        <Row size="small">
          <TextWithIcon icon="globe">
            {TimezoneHelper.convertToReadableShortCode(participant.timezone)}
          </TextWithIcon>
        </Row>
        <Row>
          <CoachingParticipantEditField
            participant={participant}
            field="whiteboardUrl"
            fieldReadableName="whiteboard link"
            iconName="link"
            assertionNames={['url']}
            editable
          />
        </Row>
        <Buttons full group>
          {onShowScheduleSessionView && (
            <Button
              level="secondary"
              data-test="add-session-button"
              onClick={() => {
                this.trackEvent('add_session_button.click');
                onShowScheduleSessionView();
              }}>
              <Text size="xxxsmall">Add session</Text>
            </Button>
          )}
          <Button
            full={!onShowScheduleSessionView}
            level="secondary"
            data-test="session-video-button"
            target="_blank"
            to={`${CoachingConstants.COACHING_SESSION_ROUTE}/${participant.distanceLearningPermalink}`}>
            <Icon name="video-camera" size="medium" />
          </Button>
        </Buttons>
        <Divider size="medium" />
        {this.renderSessionSection(
          'Upcoming',
          this.state.showUpcomingSessions,
          this.toggleUpcomingSessionsVisibility,
          this.props.upcomingSessions
        )}
        <Divider size="medium" />
        {this.renderSessionSection(
          'Past',
          this.state.showPreviousSessions,
          this.togglePreviousSessionsVisibility,
          this.props.previousSessions
        )}
        <Divider size="medium" />
        {this.renderSessionMediaSection(
          this.state.showSessionMedia,
          this.toggleSessionMediaVisibility,
          this.props.sessionMedia
        )}
        <Divider size="medium" />
      </BoxBody>
    );
  }
}
